import React, { useMemo, useRef } from 'react';
import { Platform, Pressable, StyleSheet, TextInput, View } from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';

const isWeb = Platform.OS === 'web';

function AgentsIntroHero({ onCreate, onDiscoveryY, personalContent, search, children }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS, isSM } = useResponsiveness();
  const discoveryY = useRef(0);
  const styles = useMemo(() => createStyles(colors, isXS || isSM), [colors, isXS, isSM]);

  return (
    <View style={styles.wrap}>
      <View style={styles.header}>
        <View style={styles.intro}>
          <View style={styles.eyebrow}>
            <Icon name="robot" size={18} color={colors.text_color_secondary} />
            <Text size="s" weight="semibold" color="secondary">{t('pages.agents.intro.headline')}</Text>
          </View>
          <Text tag="h1" weight="extraBold" style={styles.headline}>{t('navigation.agents')}</Text>
          <Text tag="p" color="secondary" style={styles.lead}>{t('pages.agents.intro.lead')}</Text>
          <View style={styles.heroActions}>
            <Button
              text={t('pages.agents.intro.createCta')}
              prependIcon="plus"
              variant="primary"
              size="large"
              isRound
              onPress={onCreate}
            />
          </View>
        </View>
      </View>

      {personalContent}

      <View style={styles.discover} onLayout={(event) => {
        const y = event.nativeEvent.layout.y;
        discoveryY.current = y;
        onDiscoveryY?.(y);
      }}>
        <View style={styles.discoverHead}>
          <Text tag="h2" size="xl" weight="bold">{t('pages.agents.intro.browseTitle')}</Text>
          <Text size="n" color="secondary">{t('pages.agents.intro.browseSubtitle')}</Text>
        </View>
        <View style={styles.search}>
          <Icon name="search" size={16} color={colors.text_color_secondary} />
          <TextInput
            value={search.value ?? ''}
            onChangeText={search.onChangeText}
            placeholder={search.placeholder}
            placeholderTextColor={colors.text_color_tertiary}
            style={styles.searchInput}
            autoCorrect={false}
            autoCapitalize="none"
            accessibilityLabel={search.placeholder}
          />
          {!!search.value && (
            <Pressable
              onPress={() => search.onChangeText('')}
              accessibilityRole="button"
              accessibilityLabel={t('common.clear')}
              hitSlop={8}
              style={styles.clear}
            >
              <Icon name="xmark" size={14} color={colors.text_color_secondary} />
            </Pressable>
          )}
        </View>
        {children}
      </View>
    </View>
  );
}

AgentsIntroHero.propTypes = {
  onCreate: PropTypes.func.isRequired,
  onDiscoveryY: PropTypes.func,
  onDiscoveryY: PropTypes.func,
  personalContent: PropTypes.node,
  search: PropTypes.shape({
    value: PropTypes.string,
    onChangeText: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
  }).isRequired,
  children: PropTypes.node,
};

const createStyles = (colors, compact) => StyleSheet.create({
  wrap: { width: '100%', paddingHorizontal: compact ? 16 : 24, paddingTop: compact ? 24 : 36 },
  header: { paddingBottom: compact ? 28 : 36 },
  intro: { gap: 12, maxWidth: 720 },
  eyebrow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  headline: { fontSize: compact ? 36 : 48, lineHeight: compact ? 42 : 54, letterSpacing: -1.5 },
  lead: { fontSize: 16, lineHeight: 25, maxWidth: 660 },
  heroActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    paddingTop: 8,
  },
  discover: { marginTop: compact ? 32 : 40, paddingTop: compact ? 28 : 32, borderTopWidth: 1, borderTopColor: colors.border_color_secondary, gap: 18, paddingBottom: 4 },
  discoverHead: { gap: 6 },
  search: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, minHeight: 48, borderRadius: 14, borderWidth: 1, borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_block },
  searchInput: { flex: 1, minWidth: 0, color: colors.text_color_primary, fontSize: 15, paddingVertical: 12, ...(isWeb ? { outlineStyle: 'none' } : {}) },
  clear: { width: 32, height: 32, alignItems: 'center', justifyContent: 'center' },
});

export default React.memo(AgentsIntroHero);
